import React from "react";
import { defaultFooterConfig } from "./Footer.config";
import type { FooterConfig } from "./Footer.types";
import FooterLogo from "./components/FooterLogo";
import SocialLinks from "./components/SocialLinks";
import FooterNavColumn from "./components/FooterNavColumn";
import LegalLinks from "./components/LegalLinks";
import { cn } from "@/utils/className";

interface FooterProps {
    config?: Partial<FooterConfig>;
    year?: number;
    className?: string;
}

const Footer: React.FC<FooterProps> = ({
    config = {},
    year = new Date().getFullYear(),
    className = "",
}) => {
    const footerConfig: FooterConfig = {
        ...defaultFooterConfig,
        ...config,
        logo: { ...defaultFooterConfig.logo, ...config.logo },
        socialLinks: config.socialLinks || defaultFooterConfig.socialLinks,
        navigationColumns: config.navigationColumns || defaultFooterConfig.navigationColumns,
        legalLinks: { ...defaultFooterConfig.legalLinks, ...config.legalLinks },
    };

    const {
        logo,
        companyName,
        disclaimer,
        socialLinks,
        navigationColumns,
        legalLinks,
        showTopSection = true,
        showSocialLinks = true,
        showDisclaimer = true,
        showLegalLinks = true,
        showBottomSection = true,
        copyrightBackgroundColor = "bg-[var(--color-gray-950)]",
    } = footerConfig;

    return (
        <>
            <div className={cn(showTopSection ? 'bg-primary text-white pt-2xl' : "")}>
                {showTopSection && (
                    <div className={`footer-container ${className}`}>
                        <div className="py-[6.75rem]">
                            <FooterLogo {...logo} />
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                                <div className="lg:col-span-6">
                                    {showDisclaimer && disclaimer && (
                                        <p className="text-[var(--color-gray-400)] mb-6">{disclaimer}</p>
                                    )}
                                    {showSocialLinks && <SocialLinks links={socialLinks} />}
                                </div>

                                <div className="lg:col-span-6 lg:col-start-9">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {navigationColumns.map((col, i) => (
                                            <FooterNavColumn key={i} title={col.title} links={col.links} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showBottomSection && (
                    <div className={`${copyrightBackgroundColor} text-white py-[1.56rem]`}>
                        <div className="footer-container">
                            <div className="md:flex justify-between items-center">
                                <p className="text-base">© {year} {companyName}. All Rights Reserved.</p>
                                {showLegalLinks && <LegalLinks legalLinks={legalLinks} className="mt-4 md:mt-0" />}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Footer;
